import throttle from 'lodash/throttle';
import { type ShallowRef, shallowRef } from 'vue';

interface UseFaceSelectionScrollerOptions {
  getScrollable: () => HTMLElement | null;
}

export interface FaceSelectionScroller {
  isScrollableLeftArrowVisible: ShallowRef<boolean>;
  isScrollableRightArrowVisible: ShallowRef<boolean>;
  leave: () => void;
  onSelectArrowClick: (delta: number) => void;
  onSelectScroll: () => void;
  onSelectScrollThrottled: ReturnType<typeof throttle>;
  updateScrollArrowVisibility: () => void;
}

export function useFaceSelectionScroller({
  getScrollable,
}: UseFaceSelectionScrollerOptions): FaceSelectionScroller {
  const isScrollableLeftArrowVisible = shallowRef(false);
  const isScrollableRightArrowVisible = shallowRef(false);

  function updateScrollArrowVisibility(): void {
    const scrollable = getScrollable();
    if (!scrollable) {
      return;
    }

    const scrollLeft = scrollable.scrollLeft;
    const clientWidth = scrollable.clientWidth;
    const scrollWidth = scrollable.scrollWidth;
    const scrollGap = clientWidth * 0.05;

    isScrollableLeftArrowVisible.value = scrollLeft > scrollGap;
    isScrollableRightArrowVisible.value =
      scrollLeft < scrollWidth - clientWidth - scrollGap;
  }

  function onSelectScroll(): void {
    updateScrollArrowVisibility();
  }

  const onSelectScrollThrottled = throttle(onSelectScroll, 100);

  function onSelectArrowClick(delta: number): void {
    const scrollable = getScrollable();
    if (!scrollable) {
      return;
    }

    const currentScrollLeft = scrollable.scrollLeft;
    const clientWidth = scrollable.clientWidth;
    scrollable.scrollLeft = currentScrollLeft + clientWidth * 0.8 * delta;
    updateScrollArrowVisibility();
  }

  function leave(): void {
    onSelectScrollThrottled.cancel();
  }

  return {
    isScrollableLeftArrowVisible,
    isScrollableRightArrowVisible,
    leave,
    onSelectArrowClick,
    onSelectScroll,
    onSelectScrollThrottled,
    updateScrollArrowVisibility,
  };
}
